import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, Download, Package, Search, Smartphone } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { CabecalhoPagina, Cartao, Esqueleto, Pilula } from '@/componentes/ui/primitivos';

/**
 * Versões do app coletor (APK) para baixar — a página `_AppColetor.asp`
 * dentro do WMS, no menu do usuário.
 *
 * O QR de cada versão é o caminho curto: o operador lê com o próprio coletor
 * e o download começa nele, sem cabo e sem digitar endereço. O link vai
 * direto ao S3 público, então o coletor não precisa estar logado no WMS. O
 * botão Download continua para quem baixa pelo computador.
 */
interface Versao {
  id: number;
  versao: string;
  data: string | null;
  arquivo: string;
  url: string;
  resumo: string;
  changelog: string[];
  qrSvg: string;
}

function dataBr(iso: string | null): string {
  if (!iso) return '—';
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}

export function VersoesColetor(): JSX.Element {
  const [busca, definirBusca] = React.useState('');
  const consulta = useQuery({
    queryKey: ['coletor-versoes'],
    queryFn: () => api.get<{ versoes: Versao[] }>('/api/coletor/versoes'),
    staleTime: 30_000,
  });

  const todas = consulta.data?.versoes ?? [];
  const termo = busca.trim().toLowerCase();
  const visiveis = termo
    ? todas.filter((v) =>
        [v.versao, dataBr(v.data), v.arquivo, v.resumo, ...v.changelog].join(' ').toLowerCase().includes(termo),
      )
    : todas;
  const maisRecente = todas[0]?.id;

  return (
    <div className="mx-auto max-w-[1180px] space-y-4 p-4 sm:p-6">
      <CabecalhoPagina
        icone={<Smartphone className="h-5 w-5" />}
        sobrelinha="Mega App Coletor"
        titulo="Versões App WMS Coletores"
        descricao="Leia o QR com o coletor para baixar o APK direto nele, ou use o botão Download. Da versão mais nova para a mais antiga."
        acoes={
          <label className="flex min-h-[38px] items-center gap-2 rounded-lg border border-borda bg-superficie px-2.5">
            <Search className="h-4 w-4 text-textoFraco" />
            <input
              type="search"
              value={busca}
              onChange={(e) => definirBusca(e.target.value)}
              placeholder="Buscar versão, data, mudança…"
              className="w-[220px] bg-transparent text-[13px] outline-none placeholder:text-textoFraco/70"
            />
          </label>
        }
      />

      {consulta.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Esqueleto key={i} className="h-72" />
          ))}
        </div>
      ) : consulta.isError ? (
        <Cartao className="p-4">
          <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
        </Cartao>
      ) : visiveis.length === 0 ? (
        <Cartao className="p-4">
          <EstadoVazio
            icone={<Package className="h-7 w-7" />}
            titulo={termo ? 'Nenhuma versão encontrada' : 'Nenhuma versão cadastrada'}
            detalhe={termo ? `Nada com "${busca.trim()}".` : 'O cadastro de versões do coletor está vazio.'}
          />
        </Cartao>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visiveis.map((v) => (
            <CartaoVersao key={v.id} versao={v} recente={v.id === maisRecente} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A versão mais recente é a que o coletor deve ter, e o card dela diz isso
 * sozinho: ocupa a linha inteira, faixa verde no topo, QR e botão maiores.
 * Quem abre a tela quase sempre quer só ela; as antigas ficam para trás de
 * propósito, para quem precisa voltar uma versão.
 */
function CartaoVersao({ versao: v, recente }: { versao: Versao; recente: boolean }): JSX.Element {
  return (
    <Cartao
      className={cn(
        'flex flex-col overflow-hidden',
        recente && 'shadow-forte ring-2 ring-status-livre md:col-span-2 xl:col-span-3',
      )}
    >
      <div
        className={cn(
          'flex items-start justify-between gap-2 border-b px-4 py-3',
          recente ? 'border-status-livre/30 bg-status-livreFundo' : 'border-borda',
        )}
      >
        <div className="min-w-0">
          {recente ? (
            <p className="mb-1 flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wide text-status-livre">
              <BadgeCheck className="h-4 w-4" />
              Versão atual · instale esta
            </p>
          ) : null}
          <p
            className={cn(
              'tabular font-mono font-bold leading-tight',
              recente ? 'text-[26px]' : 'text-[18px]',
            )}
          >
            v{v.versao}
          </p>
          <p className="mt-0.5 text-[12px] text-textoFraco">{dataBr(v.data)}</p>
        </div>
        {recente ? <Pilula tom="ok">Mais recente</Pilula> : null}
      </div>

      <div className={cn('flex flex-1 flex-col gap-3 px-4 py-3', recente && 'lg:flex-row lg:gap-6')}>
        <div className={cn('flex gap-3', recente && 'gap-4 lg:shrink-0')}>
          {/* SVG gerado pela API (biblioteca qrcode), não por texto de usuário. */}
          {v.qrSvg ? (
            <a
              href={v.url}
              title="Leia com o coletor para baixar"
              className={cn(
                'block shrink-0 rounded-lg border bg-white p-1 [&>svg]:h-full [&>svg]:w-full',
                recente ? 'h-[184px] w-[184px] border-status-livre/50' : 'h-[132px] w-[132px] border-borda',
              )}
              dangerouslySetInnerHTML={{ __html: v.qrSvg }}
            />
          ) : null}
          <div className={cn('min-w-0 flex-1 space-y-2', recente && 'lg:w-[300px] lg:flex-none')}>
            {v.resumo ? (
              <p className={cn('font-medium leading-snug', recente ? 'text-[14.5px]' : 'text-[13px]')}>
                {v.resumo}
              </p>
            ) : null}
            <a
              href={v.url}
              download
              className={cn(
                'inline-flex items-center gap-2 rounded-lg font-semibold text-white',
                recente
                  ? 'min-h-[44px] bg-status-livre px-4 text-[14.5px] hover:bg-status-livre/90'
                  : 'min-h-[36px] bg-primaria px-3 text-[13px] hover:bg-primaria/90',
              )}
            >
              <Download className="h-4 w-4" />
              {recente ? 'Baixar versão atual' : 'Download'}
            </a>
            <p className="truncate font-mono text-[11px] text-textoFraco" title={v.arquivo}>
              {v.arquivo}
            </p>
          </div>
        </div>

        {v.changelog.length > 0 ? (
          <div className={cn(recente && 'lg:min-w-0 lg:flex-1 lg:border-l lg:border-borda lg:pl-6')}>
            <p className="rotulo mb-1">O que mudou</p>
            <ul className="list-disc space-y-1 pl-4 text-[12.5px] leading-snug">
              {v.changelog.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Cartao>
  );
}
